import { useEffect, useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export interface FencePoint {
  lat: number;
  lng: number;
  radiusM: number;
  name?: string | null;
  isPrimary?: boolean;
}

interface Props {
  fences: FencePoint[];
  user?: { lat: number; lng: number; accuracyM?: number | null } | null;
  /** Optional explicit point to mark (e.g. mark-in location). Distinct from `user` (live coords). */
  marked?: { lat: number; lng: number; label?: string } | null;
  /** Map height in px. Default 240. Mobile auto-shrinks via CSS. */
  height?: number;
  /** True = within geofence (green user dot). False = outside (red). null = unknown. */
  within?: boolean | null;
  /** Render legend chips below the map. Default true. */
  showLegend?: boolean;
}

const fenceIcon = L.divIcon({
  className: "wm-geofence-pin",
  html: renderToStaticMarkup(
    <div
      style={{
        width: 26,
        height: 26,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--wm-accent, #2563EB)",
      }}
    >
      <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Zm0 9.5A2.5 2.5 0 1 1 14.5 9 2.5 2.5 0 0 1 12 11.5Z" />
      </svg>
    </div>,
  ),
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

const makeUserIcon = (tone: "ok" | "warn" | "neutral") => {
  const color =
    tone === "ok"
      ? "var(--wm-ok, #16A34A)"
      : tone === "warn"
        ? "var(--wm-danger, #DC2626)"
        : "var(--wm-ink-2, #475569)";
  return L.divIcon({
    className: "wm-user-dot",
    html: renderToStaticMarkup(
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: "999px",
          background: color,
          border: "3px solid #fff",
          boxShadow: `0 0 0 1px ${color}, 0 0 0 6px ${color === "var(--wm-ok, #16A34A)" ? "rgba(22,163,74,0.22)" : color === "var(--wm-danger, #DC2626)" ? "rgba(220,38,38,0.22)" : "rgba(71,85,105,0.22)"}`,
        }}
      />,
    ),
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
};

const markedIcon = L.divIcon({
  className: "wm-marked-pin",
  html: renderToStaticMarkup(
    <div
      style={{
        width: 22,
        height: 22,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--wm-warn, #D97706)",
      }}
    >
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Zm0 9.5A2.5 2.5 0 1 1 14.5 9 2.5 2.5 0 0 1 12 11.5Z" />
      </svg>
    </div>,
  ),
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

const INDIA_CENTER: [number, number] = [20.5937, 78.9629];

function AutoFit({
  fences,
  user,
  marked,
}: {
  fences: FencePoint[];
  user?: { lat: number; lng: number } | null;
  marked?: { lat: number; lng: number } | null;
}) {
  const map = useMap();
  useEffect(() => {
    const points: [number, number][] = [];
    fences.forEach((f) => {
      const r = Math.max(f.radiusM, 50) / 111_000;
      points.push([f.lat + r, f.lng]);
      points.push([f.lat - r, f.lng]);
      points.push([f.lat, f.lng + r]);
      points.push([f.lat, f.lng - r]);
    });
    if (user) points.push([user.lat, user.lng]);
    if (marked) points.push([marked.lat, marked.lng]);
    if (points.length === 0) {
      map.setView(INDIA_CENTER, 5);
      return;
    }
    const first = points[0];
    if (points.length === 1 && first) {
      map.setView(first, 17);
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [24, 24], maxZoom: 17 });
  }, [fences, user, marked, map]);
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 80);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

export function GeofenceLiveMap({
  fences,
  user,
  marked,
  height = 240,
  within,
  showLegend = true,
}: Props) {
  const userIcon = useMemo(
    () =>
      makeUserIcon(
        within === true ? "ok" : within === false ? "warn" : "neutral",
      ),
    [within],
  );
  const initialCenter: [number, number] = useMemo(() => {
    if (user) return [user.lat, user.lng];
    if (marked) return [marked.lat, marked.lng];
    if (fences[0]) return [fences[0].lat, fences[0].lng];
    return INDIA_CENTER;
  }, [user, marked, fences]);

  const userTone =
    within === true ? "ok" : within === false ? "warn" : "neutral";
  const userColor =
    userTone === "ok"
      ? "var(--wm-ok, #16A34A)"
      : userTone === "warn"
        ? "var(--wm-danger, #DC2626)"
        : "var(--wm-ink-2, #475569)";

  return (
    <div className="wm-geofence-live-wrap">
    <div
      className="wm-geofence-live-map"
      style={{
        width: "100%",
        height,
        minHeight: 160,
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid var(--wm-line, var(--line))",
        position: "relative",
        background: "#E2E8F0",
      }}
    >
      <MapContainer
        center={initialCenter}
        zoom={fences.length || user || marked ? 16 : 5}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
        zoomControl
        attributionControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {fences.map((f, i) => (
          <Circle
            key={i}
            center={[f.lat, f.lng]}
            radius={f.radiusM}
            pathOptions={{
              color: f.isPrimary ? "var(--wm-accent, #2563EB)" : "#64748B",
              weight: 2,
              dashArray: "6 5",
              fillColor: f.isPrimary ? "#2563EB" : "#64748B",
              fillOpacity: 0.08,
            }}
          />
        ))}
        {fences.map((f, i) => (
          <Marker key={`pin-${i}`} position={[f.lat, f.lng]} icon={fenceIcon} title={f.name ?? "Geofence"} />
        ))}
        {user && (
          <Marker position={[user.lat, user.lng]} icon={userIcon} title="You" />
        )}
        {user && user.accuracyM && user.accuracyM > 0 && (
          <Circle
            center={[user.lat, user.lng]}
            radius={user.accuracyM}
            pathOptions={{ color: "transparent", fillColor: "#0EA5E9", fillOpacity: 0.12 }}
          />
        )}
        {marked && (
          <Marker position={[marked.lat, marked.lng]} icon={markedIcon} title={marked.label ?? "Mark-in"} />
        )}
        <AutoFit fences={fences} user={user} marked={marked} />
      </MapContainer>
    </div>
    {showLegend && (
      <ul className="wm-geofence-legend" aria-label="Map legend">
        {fences.length > 0 && (
          <>
            <li>
              <span className="wm-leg-pin" style={{ color: "var(--wm-accent, #2563EB)" }}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Zm0 9.5A2.5 2.5 0 1 1 14.5 9 2.5 2.5 0 0 1 12 11.5Z" />
                </svg>
              </span>
              Assigned location
            </li>
            <li>
              <span
                className="wm-leg-ring"
                style={{ borderColor: "var(--wm-accent, #2563EB)", background: "rgba(37,99,235,0.08)" }}
              />
              Geofence radius
            </li>
          </>
        )}
        {user && (
          <li>
            <span
              className="wm-leg-dot"
              style={{ background: userColor, boxShadow: `0 0 0 1px ${userColor}` }}
            />
            You {within === true ? "(within)" : within === false ? "(outside)" : ""}
          </li>
        )}
        {user && user.accuracyM && user.accuracyM > 0 && (
          <li>
            <span className="wm-leg-halo" />
            GPS accuracy ±{user.accuracyM}m
          </li>
        )}
        {marked && (
          <li>
            <span className="wm-leg-pin" style={{ color: "var(--wm-warn, #D97706)" }}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Zm0 9.5A2.5 2.5 0 1 1 14.5 9 2.5 2.5 0 0 1 12 11.5Z" />
              </svg>
            </span>
            {marked.label ?? "Mark-in"}
          </li>
        )}
      </ul>
    )}
    </div>
  );
}
